import { useEffect, useMemo, useRef } from 'react';
import { useMyBotsQuery, useProjectChatsQuery } from '../../hooks/queries';
import { useOpenQueen } from '../../hooks/mutations';
import { useProjectEligibleBots } from './ComposerBotPicker';

// The bot the person chatted with last in each project: a new chat there starts with it.
const key = (projectId) => `bb.project.${projectId}.bot`;

export const lastBotIn = {
  get(projectId) {
    if (!projectId) return null;
    try { return localStorage.getItem(key(projectId)); } catch { return null; }
  },
  set(projectId, botId) {
    if (!projectId || !botId) return;
    try { localStorage.setItem(key(projectId), botId); } catch { /* private mode */ }
  },
};

/**
 * Who a new chat is with (the same everywhere, so the same bot always opens): outside a
 * project BusyBee, who answers or hands the work to the right bot and project. In a project
 * the bot the person chatted with there last (this device, else the project's latest chat),
 * else the project's first bot, else BusyBee. Changed only in the composer (ComposerBotPicker).
 */
export const pickDefaultBot = ({ project, eligible = [], projectChats = [], stored = null, queen = null }) => {
  if (!project) return queen;
  const fits = (id) => (id ? eligible.find((b) => b.id === id) : null);
  const latest = projectChats.find((c) => fits(c.bot_id));
  return fits(stored) || fits(latest?.bot_id) || eligible[0] || queen;
};

/** BusyBee herself (backend agents/queen.py), made the first time she is needed (POST /bots/queen). */
export const useQueen = ({ enabled = true } = {}) => {
  const { data: mine, isSuccess } = useMyBotsQuery();
  const openQueen = useOpenQueen();
  const make = useRef(openQueen.mutate);
  make.current = openQueen.mutate;
  const asked = useRef(false);
  const queen = mine?.queen || openQueen.data || null;
  useEffect(() => {
    if (!enabled || queen || !isSuccess || asked.current) return;
    asked.current = true;
    make.current();
  }, [enabled, queen, isSuccess]);
  return queen;
};

/**
 * The default bot for a new chat in `project` (or in none): { bot, ready }. Not ready while the
 * bots or the project's chats are still loading, so a new chat never opens with one bot and
 * switches to another a moment later. enabled: false when the caller knows the bot already
 * (nothing is loaded or made); makeQueen: false leaves BusyBee unmade until she is written to
 * (bot is null where she would answer, as in the one input, components/create/QuickInput.jsx).
 */
export const useDefaultChatBot = (project, { enabled = true, makeQueen = true } = {}) => {
  const { data: mine, isSuccess: botsLoaded } = useMyBotsQuery();
  const eligible = useProjectEligibleBots(project);
  const chats = useProjectChatsQuery(enabled ? project?.id : null);
  const chatsLoaded = !project || chats.isSuccess || chats.isError;
  const needsQueen = !project || (botsLoaded && eligible.length === 0);
  const queen = useQueen({ enabled: enabled && makeQueen && needsQueen }) || mine?.queen || null;
  return useMemo(() => {
    const bot = pickDefaultBot({
      project, eligible, projectChats: chats.data?.chats || [], stored: lastBotIn.get(project?.id), queen,
    });
    return { bot, ready: botsLoaded && chatsLoaded && !!bot, queen: mine?.queen || queen };
  }, [project, eligible, chats.data, queen, botsLoaded, chatsLoaded, mine]);
};
